import React, { Children, cloneElement } from "react";

import cx from "classnames";

import { Fieldset } from "../Fieldset";
import { Text } from "../Text";

import styles from "./RadioPill.scss";

export interface RadioPillGroupProps {
  children: JSX.Element[];
  /**
   * The id of the currently active Radio
   */
  selected: string | number | null;
  /**
   * Function called when radio is clicked
   */
  onChange: Function;
  /**
   * Displays radio group horizontal
   */
  horizontal?: boolean;
  /**
   * Label of the RadioPillGroup
   */
  label?: string;
  /**
   *  Adds test id to component
   */
  "data-testid"?: string;
  optionalLabelText?: string;
  fieldsetVariant?: "highlighted" | "transparent";
  tooltip?: string | JSX.Element;
  error?: string;
}

/**
 * The RadioPillGroup is a wrapper that can be put around RadioPill components,
 * so controlling which one is active and onChange events can be done easily on only the RadioPillGroup
 */
export function RadioPillGroup({
  children,
  selected,
  onChange,
  horizontal,
  label,
  "data-testid": dataTestId = "confetti-radio-pill-group",
  optionalLabelText,
  fieldsetVariant,
  tooltip,
  error,
}: RadioPillGroupProps) {
  const className = cx({
    [styles.horizontal]: horizontal,
    [styles.vertical]: !horizontal,
    [styles["group-of-radios"]]: label,
  });

  const radios = Children.map(children, (child, i) => {
    const testId = child.props["data-testid"]
      ? child.props["data-testid"]
      : `${dataTestId}-${i}`;

    return cloneElement(child, {
      "data-testid": testId,
      checked: child.props.id === selected,
      onChange: (
        event: React.ChangeEvent<HTMLInputElement>,
        id: string | number
      ) => {
        event.preventDefault();
        const newCoreValue = id === selected ? "" : id;
        onChange(newCoreValue);
      },
    });
  });

  return label ? (
    <Fieldset
      data-testid={dataTestId}
      label={label}
      legendType="radiogroup"
      optionalLabelText={optionalLabelText}
      variant={fieldsetVariant}
      tooltip={tooltip}
    >
      <HelperErrorText error={error} />
      <div className={className}>{radios}</div>
    </Fieldset>
  ) : (
    <div data-testid={dataTestId}>
      <HelperErrorText error={error} />
      <div className={className}>{radios}</div>
    </div>
  );
}

function HelperErrorText({ error }: { error?: string }) {
  if (!error) {
    return null;
  }

  return (
    <Text size="paragraphXsmall" paragraph className={styles["error"]}>
      {error}
    </Text>
  );
}
